const DEFAULT_TIMEOUT_MS = 15_000;

export type Primitive = string | number | boolean;

export type QueryParams = Record<string, Primitive | null | undefined>;

export interface FetchRequestConfig extends Omit<RequestInit, 'method' | 'body' | 'headers'> {
  headers?: HeadersInit;
  params?: QueryParams;
  body?: unknown;
  timeoutMs?: number;
}

export interface FetchClientConfig {
  baseUrl?: string;
  headers?: HeadersInit;
  timeoutMs?: number;
}

export interface FetchResponse<T> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

export class FetchHttpError extends Error {
  status: number;
  statusText: string;
  data: unknown;
  url: string;

  constructor({ status, statusText, data, url }: { status: number; statusText: string; data: unknown; url: string }) {
    const message =
      (typeof data === 'object' && data && 'message' in data && typeof (data as { message?: unknown }).message === 'string'
        ? (data as { message: string }).message
        : statusText) ||
      `HTTP ${status}`;
    super(message);
    this.name = 'FetchHttpError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.url = url;
  }
}

const isAbsoluteUrl = (url: string) => /^https?:\/\//.test(url);

const buildUrl = (url: string, params?: QueryParams): string => {
  if (!params) {
    return url;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const query = searchParams.toString();
  if (!query) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
};

const mergeHeaders = (...allHeaders: Array<HeadersInit | undefined>): Headers => {
  const headers = new Headers();
  allHeaders.forEach((value) => {
    if (!value) {
      return;
    }

    new Headers(value).forEach((headerValue, key) => {
      headers.set(key, headerValue);
    });
  });

  return headers;
};

const parseBody = (body: unknown, headers: Headers): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }

  if (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  ) {
    return body as BodyInit;
  }

  if (!headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return JSON.stringify(body);
};

const parseResponseData = async (response: Response): Promise<unknown> => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const joinUrl = (baseUrl: string, path: string): string => {
  if (!baseUrl || isAbsoluteUrl(path)) {
    return path;
  }

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

const createAbortController = (signal: AbortSignal | null | undefined) => {
  const controller = new AbortController();

  if (!signal) {
    return controller;
  }

  if (signal.aborted) {
    controller.abort(signal.reason);
    return controller;
  }

  signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  return controller;
};

export const createFetchClient = (config: FetchClientConfig = {}) => {
  const { baseUrl = '', headers: defaultHeaders, timeoutMs: defaultTimeoutMs = DEFAULT_TIMEOUT_MS } = config;

  const request = async <T = unknown>(method: string, path: string, requestConfig: FetchRequestConfig = {}): Promise<FetchResponse<T>> => {
    const {
      headers,
      params,
      body,
      timeoutMs = defaultTimeoutMs,
      signal,
      ...rest
    } = requestConfig;

    const url = buildUrl(joinUrl(baseUrl, path), params);
    const mergedHeaders = mergeHeaders(defaultHeaders, headers);
    const parsedBody = parseBody(body, mergedHeaders);

    const controller = createAbortController(signal);
    const timeout = setTimeout(() => {
      controller.abort(new DOMException(`Request timed out after ${timeoutMs}ms`, 'AbortError'));
    }, timeoutMs);

    try {
      const response = await fetch(url, {
        ...rest,
        method,
        headers: mergedHeaders,
        body: parsedBody,
        signal: controller.signal,
      });

      const data = await parseResponseData(response);
      if (!response.ok) {
        throw new FetchHttpError({
          status: response.status,
          statusText: response.statusText,
          data,
          url,
        });
      }

      return {
        data: data as T,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } finally {
      clearTimeout(timeout);
    }
  };

  return {
    request,
    get: <T = unknown>(path: string, requestConfig?: FetchRequestConfig) => request<T>('GET', path, requestConfig),
    post: <T = unknown>(path: string, body?: unknown, requestConfig?: FetchRequestConfig) =>
      request<T>('POST', path, { ...requestConfig, body }),
    put: <T = unknown>(path: string, body?: unknown, requestConfig?: FetchRequestConfig) =>
      request<T>('PUT', path, { ...requestConfig, body }),
    delete: <T = unknown>(path: string, requestConfig?: FetchRequestConfig) => request<T>('DELETE', path, requestConfig),
  };
};

export const fetchClient = createFetchClient({
  headers: {
    Accept: 'application/json',
  },
});
